import React, { useRef, useState, useMemo, forwardRef, useCallback } from 'react';
import { isEmpty, findIndex, forEach, isUndefined, map } from 'lodash';
import { Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import moment from 'moment';

const integrationTypes = [{
    label: 'App',
    value: 'webapp'
}, {
    label: 'Tag',
    value: 'tags'
}];


const QualysWebappMappingForm_v1 = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
    const [form] = Form.useForm();
    const projectRef = useRef({ webapp: [], tags: [] });
    const IntRef = useRef({})
    const [mappedProjectList] = useState(() => {
        if (props.record) {
            const { appId, appName, tagId, tagName } = props.record;
            const refIdArr = [appId, tagId ];
            const refNameArr = [appName, tagName ]
            const validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value));
            const _refIdArr = refIdArr[validIdIndex] ? refIdArr[validIdIndex].split(',') : [];
            const _refNameArr = refNameArr[validIdIndex] ? refNameArr[validIdIndex].split(',') : [];

            return {
                [integrationTypes[validIdIndex].value]: map(_refIdArr, (id, index) => ({ id, name: _refNameArr[index] || id }))
            }
        }
        return { webapp: [], tags: [] }
    })

    const fetchProjects = useCallback((params) => {
        const { loginConfigId, type } = params;
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: loginConfigId, type: type, ...params });
        promise.then((response) => {
            const { projects } = response;
            if(isUndefined(mappedProjectList[type])){
                mappedProjectList[type] = [];
            }
            if (mappedProjectList[type].length > 0 && !isEmpty(response.projects)) {
                response.projects = [...response.projects, ...mappedProjectList[type]];
            }
            forEach(projects, p => {
                switch(type){
                    case "webapp":{
                        const appUrl = p.otherProperties?.appUrl;
                        projectRef.current[type][p.id] = p.name + (!isEmpty(appUrl) ? ' - ' + appUrl : '');
                        break;
                    }
                    case "tags":{
                        const createdDate = p.otherProperties?.created;
                        projectRef.current[type][p.id] = p.name + (!isEmpty(createdDate) ? ' - ' + moment(createdDate).format('MMM DD YYYY') : '');
                        break;
                    }
                    default: break;
                }
            });

        });
        return { promise, cancel };

    }, [props.toolName, mappedProjectList])


    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { appId, appName, tagId, tagName , discovery_config_id} = config;
        const refIdArr = [appId, tagId ];
        const refNameArr = [appName, tagName ]
        const validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value))

        return ({
            projectId: refIdArr[validIdIndex]?.split(','),
            projectName: refNameArr[validIdIndex]?.split(','),
            integration: integrationTypes[validIdIndex].value,
            ...(discovery_config_id && {discovery_config_id: discovery_config_id}),
        });
    }, []);

    const onIntegrationTypeChange = useCallback((value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        const updatedConfiguration = [
            ...configurations,
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            integration: value,
            projectId: undefined
        }

        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form]);

    const getExtraFieldsData = useCallback((config) => {
        const { projectId, integration, discovery_config_id } = config;
        const selectedProjectList = []
        projectId.forEach(id => {
            if (projectRef.current[integration] && projectRef.current[integration][id]) {
                selectedProjectList.push(projectRef.current[integration][id]);
            }
        })
        const selectedProject = selectedProjectList.join(",")
        const extraFieldData = { ...(discovery_config_id && {discovery_config_id: discovery_config_id}),}
        switch (integration) {
            case 'webapp':
                extraFieldData.appId = projectId?.join(",")
                extraFieldData.appName = selectedProject

                break;
            case 'tags':
                extraFieldData.tagId = projectId?.join(",")
                extraFieldData.tagName = selectedProject

                break;
              
            default:
                break;
        }
        return extraFieldData
    }, []);

    const isProjectFieldDisabled = useCallback((fieldKey, form) => {
        const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
        return (!fieldConfiguration || !fieldConfiguration.integration);
    }, []);

    const extraFields = useMemo(() => ([
        {
            name: 'integration',
            colSpan: '6',
            type: FieldTypes.SELECT,
            label: 'Integration Type',
            options: integrationTypes,
            disabled: disableProjectFields,
            required: true,
            props: {
                onChange: onIntegrationTypeChange,
                autoFocus: !disableProjectFields
            }
        },
        {
            name: 'projectId',
            colSpan: "18",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Qualys WebApp App/Tag',
            propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
            required: true,
            props: {
                mode: "multiple",
                setExtraProps: (field, listField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const type = allConfigs[listField.name]?.integration;
                    if(!IntRef.current[type]){
                        IntRef.current[type] = { login_id: props.loginConfigId, type };
                    }
                    return {
                        api: type && fetchProjects,
                        apiParams: IntRef.current[type],
                        resetOptionsProp: type,
                        parseSelectOptions: parseSelectOptions
                    }

                }
            }
        }
    ]), [form, onIntegrationTypeChange, isProjectFieldDisabled, disableProjectFields,props.loginConfigId, fetchProjects]);


    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            {...props}
        />
    )
})

export default QualysWebappMappingForm_v1;

const parseSelectOptions = (options) => {
    return map(options, (option) => {
        let label = option.name || option.id;
        const value = option.id;
        if(option.otherProperties?.created){
            const createdDate = option.otherProperties?.created;
            if(createdDate){
                const formattedDate = moment(createdDate).format('MMM DD YYYY');
                label += " - " + formattedDate;
            }
        }
        else if(option.otherProperties?.appUrl){
            const appUrl = option.otherProperties?.appUrl;
            if(appUrl){
                label += " - " + appUrl;
            }
        }

        return { label, value };
    })
}